import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { employeeDB, EmployeeRecord, autoMapEmployeesToApiKeys } from '../../../utils/employee-indexeddb';
import type { CsvPreview } from '../../../utils/csv-parser';
import { CsvColumnMapping, autoDetectMappings, csvToEmployeeRecords, exportToCsv, generateCsvTemplate, parseCSV, readCsvFile, validateCsvData } from '../../../utils/csv-parser';
import {
  HEADQUARTER_FILTER_PREFIX,
  DEPARTMENT_FILTER_PREFIX,
  UNASSIGNED_HEADQUARTER,
  parseDepartmentHierarchy
} from '../../../utils/department-utils';
import { generateHeadquarterHtmlReport, downloadHtmlReport } from '../../../utils/html-report-generator';
import type { Statistics, UseEmployeeMappingReturn } from '../types';
import { loadMappingApiKeys } from '../../../utils/mapping-api-keys';

export const useEmployeeMapping = (): UseEmployeeMappingReturn => {
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // CSV Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<CsvPreview | null>(null);
  const [mappings, setMappings] = useState<CsvColumnMapping[]>([]);
  const [importProgress, setImportProgress] = useState<string>('');

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [showOnlyMapped, setShowOnlyMapped] = useState(false);

  const normalizedDepartmentFilter = selectedDepartment;

  const headquarterSelectValue = (() => {
    if (normalizedDepartmentFilter.startsWith(DEPARTMENT_FILTER_PREFIX)) {
      const [, hq] = normalizedDepartmentFilter.split('::');
      return `${HEADQUARTER_FILTER_PREFIX}${hq || ''}`;
    }
    if (normalizedDepartmentFilter.startsWith(HEADQUARTER_FILTER_PREFIX)) {
      return normalizedDepartmentFilter;
    }
    return 'all';
  })();

  const activeHeadquarter = headquarterSelectValue.startsWith(HEADQUARTER_FILTER_PREFIX)
    ? headquarterSelectValue.slice(HEADQUARTER_FILTER_PREFIX.length)
    : '';

  const departmentSelectValue = activeHeadquarter && normalizedDepartmentFilter.startsWith(DEPARTMENT_FILTER_PREFIX)
    ? normalizedDepartmentFilter
    : activeHeadquarter
      ? `${HEADQUARTER_FILTER_PREFIX}${activeHeadquarter}`
      : 'all';

  const isDepartmentSelectionDisabled = !activeHeadquarter;

  const { headquarterOptions, departmentOptions, roleOptions } = useMemo(() => {
    const headquarterMap = new Map<string, Set<string>>();
    const roleSet = new Set<string>();

    employees.forEach(employee => {
      let headquarter = employee.headquarter;
      let departmentUnit = employee.departmentUnit;
      let normalizedDepartment = employee.normalizedDepartment;

      if (!headquarter || (!departmentUnit && !normalizedDepartment)) {
        const parsed = parseDepartmentHierarchy(employee.department);
        headquarter = headquarter || parsed.headquarter;
        departmentUnit = departmentUnit || parsed.department;
        normalizedDepartment = normalizedDepartment || parsed.normalized;
      }

      if (!headquarter && departmentUnit) {
        headquarter = UNASSIGNED_HEADQUARTER;
      }

      if (headquarter) {
        if (!headquarterMap.has(headquarter)) {
          headquarterMap.set(headquarter, new Set<string>());
        }
        if (departmentUnit) {
          headquarterMap.get(headquarter)!.add(departmentUnit);
        }
        // If no department unit but normalized info differs, capture it as well
        if (!departmentUnit && normalizedDepartment && normalizedDepartment !== headquarter) {
          headquarterMap.get(headquarter)!.add(normalizedDepartment);
        }
      }

      if (employee.role) {
        roleSet.add(employee.role);
      }
    });

    const hqOptions = Array.from(headquarterMap.keys()).sort((a, b) => a.localeCompare(b, 'ja'));
    const deptOptions = new Map<string, string[]>();
    hqOptions.forEach(hq => {
      const departments = Array.from(headquarterMap.get(hq) || [])
        .sort((a, b) => a.localeCompare(b, 'ja'));
      deptOptions.set(hq, departments);
    });

    return {
      headquarterOptions: hqOptions,
      departmentOptions: deptOptions,
      roleOptions: Array.from(roleSet).sort((a, b) => a.localeCompare(b, 'ja'))
    };
  }, [employees]);

  const employeeMatchesOrganizationFilter = useCallback((employee: EmployeeRecord, filterValue: string): boolean => {
    if (!filterValue || filterValue === 'all') {
      return true;
    }

    let headquarter = employee.headquarter;
    let departmentUnit = employee.departmentUnit;
    let normalizedDepartment = employee.normalizedDepartment;

    if (!headquarter || (!departmentUnit && !normalizedDepartment)) {
      const parsed = parseDepartmentHierarchy(employee.department);
      headquarter = headquarter || parsed.headquarter;
      departmentUnit = departmentUnit || parsed.department;
      normalizedDepartment = normalizedDepartment || parsed.normalized;
    }

    if (!headquarter && departmentUnit) {
      headquarter = UNASSIGNED_HEADQUARTER;
    }

    if (filterValue.startsWith(HEADQUARTER_FILTER_PREFIX)) {
      const selectedHQ = filterValue.slice(HEADQUARTER_FILTER_PREFIX.length);
      return headquarter === selectedHQ;
    }

    if (filterValue.startsWith(DEPARTMENT_FILTER_PREFIX)) {
      const [, selectedHQ, selectedDept] = filterValue.split('::');
      if (!selectedHQ) {
        return false;
      }

      if (headquarter !== selectedHQ) {
        return false;
      }

      if (!selectedDept) {
        return true;
      }

      const targets = [
        departmentUnit || '',
        normalizedDepartment || '',
        employee.department || ''
      ];

      const trimmedDept = selectedDept.trim();
      return targets.some(target => target.trim() === trimmedDept || target.includes(trimmedDept));
    }

    const fallback = employee.department || '';
    const trimmedFilter = filterValue.trim();
    return fallback.trim() === trimmedFilter || fallback.includes(trimmedFilter);
  }, []);

  // API Keys state
  const [availableApiKeys, setAvailableApiKeys] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedApiKey, setSelectedApiKey] = useState<{ [email: string]: string }>({});

  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadEmployeeData = useCallback(async () => {
    setLoading(true);
    try {
      await employeeDB.init();
      const [allEmployees, stats] = await Promise.all([
        employeeDB.getAllEmployees(),
        employeeDB.getStatistics()
      ]);
      setEmployees(allEmployees);
      setStatistics(stats);
    } catch (err) {
      console.error('Failed to load employee data:', err);
      setError('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle file selection
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCsvFile(file);
    setError(null);

    try {
      const content = await readCsvFile(file);
      const parsed = parseCSV(content);

      // Validate CSV
      const validation = validateCsvData(parsed);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }

      if (validation.warnings.length > 0) {
        console.warn('CSV warnings:', validation.warnings);
      }

      // Auto-detect mappings
      const detectedMappings = autoDetectMappings(parsed.headers);
      setMappings(detectedMappings);

      // Set preview (first 10 rows)
      setCsvPreview({
        headers: parsed.headers,
        rows: parsed.rows.slice(0, 10),
        totalRows: parsed.data.length
      });

      setShowImportModal(true);
    } catch (err) {
      console.error('Failed to read CSV file:', err);
      setError(err instanceof Error ? err.message : 'Failed to read CSV file');
    }
  };

  // Handle CSV import
  const handleImport = async () => {
    if (!csvFile || !csvPreview) return;

    setLoading(true);
    setImportProgress('Reading CSV file...');

    try {
      const content = await readCsvFile(csvFile);
      const parsed = parseCSV(content);

      setImportProgress('Converting to employee records...');
      const records = csvToEmployeeRecords(parsed.data, mappings);

      setImportProgress(`Importing ${records.length} records...`);
      const result = await employeeDB.saveEmployees(records);

      // Save import history
      await employeeDB.saveImportHistory({
        fileName: csvFile.name,
        totalRecords: records.length,
        matchedRecords: result.saved,
        updatedRecords: result.saved,
        failedRecords: result.failed.length,
        importedAt: new Date().toISOString()
      });

      setSuccess(`Successfully imported ${result.saved} employees. ${result.failed.length} failed.`);
      setShowImportModal(false);
      setCsvFile(null);
      setCsvPreview(null);

      // Reload data
      await loadEmployeeData();

      // Notify other components
      window.dispatchEvent(new CustomEvent('employeeDataChanged'));

      // Auto-map to API keys
      setImportProgress('Auto-mapping to API keys...');
      await handleAutoMap();

    } catch (err) {
      console.error('Import failed:', err);
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setLoading(false);
      setImportProgress('');
    }
  };

  // Handle auto-mapping to API keys
  const handleAutoMap = async () => {
    setLoading(true);
    setImportProgress('Fetching API keys...');

    try {
      // Get API keys from the usage dashboard data
      // This would need to be passed from the parent or fetched
      const apiKeys = await fetchApiKeys();

      setImportProgress('Mapping employees to API keys...');
      const result = await autoMapEmployeesToApiKeys(apiKeys, {
        domainAutoComplete: true,
        partialMatch: true,
        caseSensitive: false
      });

      setSuccess(`Mapped ${result.matched} API keys. ${result.unmatched.length} unmatched.`);

      // Reload data
      await loadEmployeeData();
    } catch (err) {
      console.error('Auto-mapping failed:', err);
      setError('Failed to auto-map employees to API keys');
    } finally {
      setLoading(false);
      setImportProgress('');
    }
  };

  // Load available API keys
  const loadAvailableApiKeys = useCallback(async () => {
    try {
      setAvailableApiKeys(await loadMappingApiKeys());
    } catch (err) {
      setAvailableApiKeys([]);
      setError(err instanceof Error ? err.message : 'Failed to load API key candidates');
    }
  }, []);

  useEffect(() => {
    void loadEmployeeData();
    void loadAvailableApiKeys();
  }, [loadEmployeeData, loadAvailableApiKeys]);

  const fetchApiKeys = loadMappingApiKeys;

  // Handle manual mapping
  const handleManualMap = async (employee: EmployeeRecord, apiKeyId: string, apiKeyName: string) => {
    try {
      const success = await employeeDB.mapEmployeeToApiKey(employee.email, apiKeyId, apiKeyName);
      if (success) {
        setSuccess(`Mapped ${employee.name} to ${apiKeyName}`);
        await loadEmployeeData();
        window.dispatchEvent(new CustomEvent('employeeDataChanged'));
      } else {
        setError('Failed to map employee');
      }
    } catch (err) {
      console.error('Mapping failed:', err);
      setError('Failed to map employee');
    }
  };

  // Handle delete employee
  const handleDeleteEmployee = async (email: string) => {
    if (!confirm('Are you sure you want to delete this employee record?')) return;

    try {
      const success = await employeeDB.deleteEmployee(email);
      if (success) {
        setSuccess('Employee record deleted');
        await loadEmployeeData();
        window.dispatchEvent(new CustomEvent('employeeDataChanged'));
      } else {
        setError('Failed to delete employee record');
      }
    } catch (err) {
      console.error('Delete failed:', err);
      setError('Failed to delete employee record');
    }
  };

  // Handle reset all employees
  const handleResetAll = async () => {
    if (!confirm('全ての社員データをリセットしますか？この操作は取り消せません。')) return;

    try {
      const success = await employeeDB.clearAllEmployees();
      if (success) {
        setSuccess('All employee data has been reset');
        await loadEmployeeData();
        window.dispatchEvent(new CustomEvent('employeeDataChanged'));
      } else {
        setError('Failed to reset employee data');
      }
    } catch (err) {
      console.error('Reset failed:', err);
      setError('Failed to reset employee data');
    }
  };

  // Handle export
  const handleExport = () => {
    const exportData = employees.map(emp => ({
      '社員番号': emp.employeeId || '',
      '氏名': emp.name || '',
      '氏名カナ': emp.nameKana || '',
      '役職': emp.role || '',
      'メールアドレス': emp.email,
      '社用携帯': emp.phone || '',
      '所属': emp.department || '',
      '入社日': emp.joinDate || '',
      'API Key ID': emp.apiKeyId || '',
      'API Key Name': emp.apiKeyName || '',
      '最終更新': emp.lastUpdated
    }));

    exportToCsv(exportData, `employee_mapping_${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Handle HTML report export
  const handleExportHtmlReport = () => {
    const htmlContent = generateHeadquarterHtmlReport(
      employees,
      'API利用状況報告',
      '過去30日間の利用状況'
    );

    const now = new Date();
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const fileName = `api_usage_report_${dateStr}.html`;
    downloadHtmlReport(htmlContent, fileName);

    setSuccess('HTMLレポートをダウンロードしました');
  };

  // Handle template download
  const handleDownloadTemplate = () => {
    const template = generateCsvTemplate();
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'employee_template.csv');
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  // Handle headquarter change
  const handleHeadquarterChange = (value: string) => {
    if (value === 'all') {
      setSelectedDepartment('all');
    } else if (value.startsWith(HEADQUARTER_FILTER_PREFIX)) {
      setSelectedDepartment(value);
    } else {
      setSelectedDepartment('all');
    }
  };

  // Handle department change
  const handleDepartmentChange = (value: string) => {
    if (!activeHeadquarter) {
      setSelectedDepartment('all');
      return;
    }

    if (value === `${HEADQUARTER_FILTER_PREFIX}${activeHeadquarter}`) {
      setSelectedDepartment(value);
      return;
    }

    if (value.startsWith(DEPARTMENT_FILTER_PREFIX)) {
      setSelectedDepartment(value);
      return;
    }

    if (value === 'all') {
      setSelectedDepartment('all');
    }
  };

  // Filter employees (memoized)
  const filteredEmployees = useMemo(() => employees.filter(emp => {
    if (searchTerm && !emp.email.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !(emp.name && emp.name.toLowerCase().includes(searchTerm.toLowerCase()))) {
      return false;
    }
    if (!employeeMatchesOrganizationFilter(emp, selectedDepartment)) {
      return false;
    }
    if (selectedRole !== '') {
      if (!emp.role) return false;

      try {
        const regex = new RegExp(selectedRole, 'i');
        if (!regex.test(emp.role)) {
          return false;
        }
      } catch  {
        // If regex is invalid, fall back to simple string matching
        if (!emp.role.toLowerCase().includes(selectedRole.toLowerCase())) {
          return false;
        }
      }
    }
    if (showOnlyMapped && !emp.apiKeyId) {
      return false;
    }
    return true;
  }), [employeeMatchesOrganizationFilter, employees, searchTerm, selectedDepartment, selectedRole, showOnlyMapped]);

  return {
    // Data
    employees,
    statistics,
    filteredEmployees,
    availableApiKeys,

    // Loading/status
    loading,
    error,
    success,
    importProgress,

    // CSV Import
    showImportModal,
    csvPreview,
    mappings,

    // Filter state
    searchTerm,
    selectedDepartment,
    selectedRole,
    showOnlyMapped,

    // Organization filter derived values
    headquarterSelectValue,
    activeHeadquarter,
    departmentSelectValue,
    isDepartmentSelectionDisabled,
    headquarterOptions,
    departmentOptions,
    roleOptions,

    // API Key selection state
    selectedApiKey,

    // Refs
    fileInputRef,

    // Actions
    setError,
    setSuccess,
    setSearchTerm,
    setSelectedDepartment,
    setSelectedRole,
    setShowOnlyMapped,
    setSelectedApiKey,
    setMappings,
    setShowImportModal,
    setCsvFile,
    setCsvPreview,

    // Handlers
    handleFileSelect,
    handleImport,
    handleAutoMap,
    handleManualMap,
    handleDeleteEmployee,
    handleResetAll,
    handleExport,
    handleExportHtmlReport,
    handleDownloadTemplate,
    handleHeadquarterChange,
    handleDepartmentChange,
  };
};
